import { Circle, Fill, Stroke, Style, Text } from 'ol/style';
import { DEVICE_PIXEL_RATIO } from 'ol/has';
import { getKat } from '../formal';

const getArealbrukFill = (code, alpha) => {
  const at = formål2gk(code).Arealtype;
  let colour = 'transparent' // Skog will be transparent, for instance.
  if (at === 'Annen utmark' ) colour = [255, 255, 255, alpha];
  else if (at === 'Beite') colour = [255, 255, 55, alpha];
  else if (at === 'Dyrket mark') colour = [251, 208, 100, alpha];
  else if (at === 'Utbygd areal') colour = [247, 219, 216, alpha];
  else if (at === 'Vann og myr') {
    const ferskvann = (code === 6500); // Drikkevann <--> formål 6500
    // const ferskvann = (at_ar5 === 81); // Ferskvann <--> arealtype 81
    if (!ferskvann) colour = [209, 209, 255, alpha];
    else colour = [145, 231, 255, alpha];
  }
  return new Fill({ color: colour });
};

const getTreslagFill = (code, alpha) => {
  const ts = formål2gk(code).Treslag;
  let colour = 'transparent';
  switch (ts) {
    case 'Barskog':
      colour = [125, 191, 110, alpha];
      break;
    case 'Lauvskog':
      colour = [128, 255, 8, alpha];
      break;
    case 'Blandingsskog':
      colour = [158, 204, 115, alpha];
      break;
    default:
      colour = 'transparent';
  }
  return new Fill({ color: colour });
};

const getSkogbonitetFill = (code, alpha) => {
  const sb = formål2gk(code).Skogbonitet;
  let colour = 'transparent';
  switch (sb) {
    case 'Særs høy':
      colour = dots(4, alpha);
      break;
    case 'Høy':
      colour = dots(6, alpha);
      break;
    case 'Middels':
      colour = dots(8, alpha);
      break;
    case 'Lav':
      colour = dots(10, alpha);
      break;
    default:
      colour = 'transparent';
  }
  return new Fill({ color: colour });
};

const getOrganiskStroke = (mito) => {
  const organisk = (mito === 2);
  const colour = 'rgb(133, 182, 240)';
  return organisk ? new Stroke({ color: colour, width: 4 }) : dStroke;
};

const getBebygdFill = (kn1, kn2, alpha) => {
  let colour = 'transparent';
  switch (kn2) {
    case 'Boligområde':
    case 'Boligområde - Spredt':
    case 'Boligområde - Tett':
      colour = [215, 181, 216, alpha];
      break; // Bolig
    case 'Samferdselsområde - Bane':
    case 'Samferdselsområde - Flyplass':
    case 'Samferdselsområde - Havn':
    case 'Samferdselsområde - Terminal':
    case 'Samferdselsområde - Vei':
      colour = [145, 82, 45, alpha];
      break; // Samferdselsområde
    case 'Nærings- og institusjonsområde':
    case 'Nærings- og institusjonsområde - Reiseliv (Camping/Hytter)':
      colour = [223, 101, 176, alpha];
      break; // Nærings- og institusjonsområde
    case 'Fritidsboligområde':
      colour = [247, 219, 216, alpha];
      break; // Hytteområde
    case 'Idrettsområde - Bebygd':
    case 'Idrettsområde':
      colour = [223, 194, 125, alpha];
      break; // Idrettsområde
    case 'Grøntområde':
      colour = (kn1 === 'Uklassifisert') ? 'transparent' : [215, 239, 210, alpha];
      break; // Grøntområde
    case 'Bebygd':
      colour = [186, 186, 186, alpha];
      break; // Uklassifisert område
    case 'Infrastruktur':
      colour = [123, 50, 148, alpha];
      break; // Infrastrukturområde
    default:
      colour = 'transparent';
  }
  return new Fill({ color: colour });
};

// Generate a canvasPattern with circles on transparent background:
const dots = (gap, alpha) => {
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  // Gradient and pattern are in canvas pixel space, therefore we adjust for the
  // renderer's pixel ratio:
  const pixelRatio = DEVICE_PIXEL_RATIO; // did not work on mac FF
  // const pixelRatio = 1;

  // Note: for seamless repeat patterns, image width and height of the
  // pattern image must be a factor of two (2, 4, 8, ..., 512).
  const side = gap * pixelRatio;
  canvas.width = side;
  canvas.height = side;
  // Transparent background:
  context.fillStyle = 'transparent';
  context.fillRect(0, 0, canvas.width, canvas.height);
  // Black circle:
  context.fillStyle = 'black';
  context.globalAlpha = alpha;
  context.beginPath();
  const centre = [side / 2, side / 2];
  const radius = .9 * pixelRatio;
  context.arc(centre[0], centre[1], radius, 0, 2 * Math.PI);
  context.fill();
  return context.createPattern(canvas, 'repeat');
};

// Defaul style (non suitable features/plans):
const whiteFill = new Fill({ color: 'rgba(255, 255, 255, .4)' });
const dFill = new Fill({ color: 'transparent' });
const dStroke = new Stroke({ color: '#3399CC', width: 1.25 });
const dStyle = new Style({
  image: new Circle({ fill: dFill, stroke: dStroke, radius: 5 }),
  fill: dFill,
  stroke: dStroke
});

const thickerText = (text) => {
  // return new Text({ text: text });
  return new Text({
    fill: new Fill({ color: 'yellow' }),
    stroke: new Stroke({ color: 'gray', width: 3 }),
    text: text,
    font: 'normal italic 16px helvetica',
    textBaseline: 'ideographic',
    offsetY: -12
  });
};

export const highlightStyle = (h, dash) => {
  const c = (typeof h === 'string' || h instanceof String) ? h : 'red';
  return new Style({
    // fill: new Fill({ color: 'rgba(255, 0, 0, .1)' }),
    stroke: new Stroke({ color: c, lineDash: dash ?? null, width: 2 }),
    zIndex: 1
  })
};

export const planStyle = (feature) => {
  const p = feature.getProperties();
  const opacity_percentage = p.opacity ?? 100;
  const alpha = opacity_percentage / 100;
  const styleStack = [];
  if (p.checked && p.suitable && p.arealformål) {
    const nå = (p.plankategori === 'Nåværende');
    // const arealbruk = new Style({ fill: getArealbrukFill(p.formål, alpha) });
    // const treslag = new Style({ fill: getTreslagFill(p.formål, alpha) });
    // const skogbonitet = new Style({ fill: getSkogbonitetFill(p, alpha) });
    const organisk = new Style({ stroke: getOrganiskStroke(p) });
    const f = nå ? whiteFill : getBebygdFill(p.kat_nivå1, p.kat_nivå2, alpha);
    const bebygd = new Style({ fill: f });
    // const kraftlinje = getKraftlinjeStyle(p);
    const text = new Style({ text: thickerText(p.feltnavn) });
    // styleStack.concat([arealtype, treslag, skogbonitet, grunnforhold]);
    // [arealbruk, treslag, skogbonitet, organisk, bebygd, kraftlinje, text]
    [organisk, bebygd, text].forEach(s => styleStack.push(s));
  } else if (['KpOmråde', 'RpOmråde'].indexOf(p.planType) > -1 || p.drawn) {
    const n = p.NASJONALAREALPLANID;
    const planid = (n && n.planidentifikasjon) || '';
    // styleStack.concat([dStyle, new Style({ text: thickerText(planid) })]);
    [dStyle, new Style({ text: thickerText(planid), zIndex: 1 })].forEach(
      s => styleStack.push(s)
    );
  }
  if (p.highlight) styleStack.push(highlightStyle(p.highlight));
  return styleStack;
};
